import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Mutation,
  PageInfo,
  ProgrammingStructure,
  ProgrammingStructureFilterInput,
  Query,
} from "@models/ISchema";
import { CreateProgrammingStructureInput } from "@services/types/programming-structures/programmingStructuresTypes";
import { PROGRAMMING_STRUCTURES_QUERY } from "./graphql/programmingStructures.query";
import { PROGRAMMING_STRUCTURES_STAGES_QUERY } from "./graphql/programmingStructuresStages.query";
import { CREATE_PROGRAMMING_STRUCTURE } from "./graphql/createProgrammingStructures.mutation";
import { GET_PROGRAMMING_STRUCTURE_BY_ID } from "./graphql/getProgrammingStructure.query";
import { UPDATE_PROGRAMMING_STRUCTURE } from "./graphql/updateProgrammingStructure.mutation";
import { DELETE_PROGRAMMING_STRUCTURE } from "./graphql/deleteProgrammingStructure";

export const useGetProgrammingStructures = () => {
  const [getProgrammingStructures, { loading, data }] = useLazyQuery<Query>(
    PROGRAMMING_STRUCTURES_QUERY,
  );

  const handleGetProgrammingStructures = ({
    scenarioId,
    filter,
  }: {
    scenarioId: string;
    filter: ProgrammingStructureFilterInput;
  }) => {
    getProgrammingStructures({
      variables: {
        scenarioId: scenarioId,
        filter,
      },
    });
  };

  return {
    getProgrammingStructures: handleGetProgrammingStructures,
    loading,
    programmingStructures: (data?.cube?.programmingStructures?.items ??
      []) as ProgrammingStructure[],
    pageInfo: (data?.cube?.programmingStructures?.pageInfo ?? {}) as PageInfo,
  };
};

export const useGetProgrammingStructuresStages = () => {
  const [getProgrammingStructuresStages, { loading, data }] = useLazyQuery<Query>(
    PROGRAMMING_STRUCTURES_STAGES_QUERY,
  );

  const handleGetProgrammingStructuresStages = (scenarioId: string) => {
    getProgrammingStructuresStages({
      variables: {
        scenarioId: scenarioId,
      },
    });
  };

  return {
    getProgrammingStructuresStages: handleGetProgrammingStructuresStages,
    loading,
    programmingStructuresStages: data?.data?.programmingStructureStages?.items ?? [],
    pageInfo: data?.data?.programmingStructureStages?.pageInfo ?? ({} as PageInfo),
  };
};

export const useCreateProgrammingStructure = () => {
  const [createProgrammingStructure, { loading, data }] = useMutation<Mutation>(
    CREATE_PROGRAMMING_STRUCTURE,
  );

  const handleCreateProgrammingStructure = ({
    scenarioId,
    originId,
    input,
  }: CreateProgrammingStructureInput) =>
    createProgrammingStructure({
      variables: {
        scenarioId,
        originId,
        input,
      },
    });

  return {
    createProgrammingStructure: handleCreateProgrammingStructure,
    loading,
    data: data?.cube?.programmingStructuresCrud,
  };
};

export const useUpdateProgrammingStructure = () => {
  const [updateProgrammingStructure, { loading, data }] = useMutation<Mutation>(
    UPDATE_PROGRAMMING_STRUCTURE,
  );

  const handleUpdateProgrammingStructure = ({
    scenarioId,
    originId,
    input,
  }: CreateProgrammingStructureInput) =>
    updateProgrammingStructure({
      variables: {
        scenarioId,
        originId,
        input,
      },
    });

  return {
    updateProgrammingStructure: handleUpdateProgrammingStructure,
    loading,
    data: data?.cube?.programmingStructuresCrud,
  };
};

export const useDeleteProgrammingStructure = () => {
  const [deleteProgrammingStructure, { loading, data }] = useMutation<Mutation>(
    DELETE_PROGRAMMING_STRUCTURE,
  );

  const handleDeleteProgrammingStructure = ({
    scenarioId,
    originId,
    input,
  }: CreateProgrammingStructureInput) =>
    deleteProgrammingStructure({
      variables: {
        scenarioId,
        originId,
        input,
      },
    });

  return {
    deleteProgrammingStructure: handleDeleteProgrammingStructure,
    loading,
    data: data?.cube?.programmingStructuresCrud,
  };
};

export const useGetProgrammingStructureById = () => {
  const [getProgrammingStructureById, { loading, data }] = useLazyQuery<Query>(
    GET_PROGRAMMING_STRUCTURE_BY_ID,
  );

  const handleGetProgrammingStructureById = (
    scenarioId: string | number,
    programmingStructureId: string | number,
  ) => {
    getProgrammingStructureById({
      variables: {
        scenarioId,
        programmingStructureId,
      },
    });
  };

  return {
    getProgrammingStructureById: handleGetProgrammingStructureById,
    loading,
    programmingStructure: data?.cube?.programmingStructure,
  };
};
